import { useState, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { Button, Tabs, TabsList, TabsTrigger } from '@pantry/ui';
import { PlusIcon } from 'lucide-react';
import { useShoppingLists, useFeatureAccess } from '@pantry/api-client';
import { FeatureFlag } from '@pantry/shared';
import { AnalyticsEvents } from '@pantry/analytics';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  ShoppingListCard,
  ShoppingListCardSkeleton,
} from '../../components/shopping-lists/ShoppingListCard';
import { CreateListDialog } from '../../components/shopping-lists/CreateListDialog';
import { EmptyState } from '../../components/shopping-lists/EmptyState';
import { UpgradeDialog } from '../../components/premium/UpgradeDialog';

export const Route = createFileRoute('/_app/shopping-lists')({
  component: ShoppingListsPage,
});

type TabValue = 'active' | 'archived';

function ShoppingListsPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { selectedHousehold } = useSelectedHousehold();
  const householdId = selectedHousehold?.id ?? '';

  const [activeTab, setActiveTab] = useState<TabValue>('active');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const isActive = activeTab === 'active';

  const {
    data: lists,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useShoppingLists(householdId, { isActive });

  // Always query active lists for the premium limit check, regardless of current tab
  const { data: activeListsForLimit } = useShoppingLists(householdId, { isActive: true });

  // Premium gating for multiple lists
  const flagEnabled = useFeatureFlag(FeatureFlag.SHOPPING_LISTS_PREMIUM);
  const { hasAccess: hasPremium } = useFeatureAccess(flagEnabled);

  // Sort: pinned first → then by updatedAt desc
  const sortedLists = useMemo(
    () =>
      [...(lists ?? [])].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }),
    [lists]
  );

  const { track } = useAnalytics();
  const activeListCount = activeListsForLimit?.length ?? 0;

  const handleCreateClick = () => {
    if (!householdId) return;
    // Free users are limited to 1 list
    if (!hasPremium && activeListCount >= 1) {
      track(AnalyticsEvents.SHOPPING_LIST_UPGRADE_PROMPTED, { householdId, trigger: 'list_limit' });
      setShowUpgradeDialog(true);
      return;
    }
    setShowCreateDialog(true);
  };

  const handleCreateSuccess = (listId: string) => {
    void navigate({ to: '/shopping-lists/$listId', params: { listId } });
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">
            {t({ message: 'Shopping Lists', context: 'page_title' })}
          </h1>
          {!isLoading && lists !== undefined && (
            <p className="text-sm text-muted-foreground mt-1">
              <Plural value={lists.length} one="# list" other="# lists" />
            </p>
          )}
        </div>
        <Button onClick={handleCreateClick}>
          <PlusIcon className="h-4 w-4 mr-2" />
          <Trans context="action_button">Create List</Trans>
        </Button>
      </div>

      {/* Active / Archived toggle */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
        <TabsList>
          <TabsTrigger value="active">
            <Trans>Active</Trans>
          </TabsTrigger>
          <TabsTrigger value="archived">
            <Trans>Archived</Trans>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ShoppingListCardSkeleton />
          <ShoppingListCardSkeleton />
          <ShoppingListCardSkeleton />
        </div>
      ) : error ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : sortedLists.length === 0 ? (
        <EmptyState
          variant={isActive ? 'no-lists' : 'no-archived'}
          onCreateList={isActive ? handleCreateClick : undefined}
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedLists.map((list) => (
              <ShoppingListCard
                key={list.id}
                list={list}
                householdId={householdId}
                hasPremium={hasPremium}
              />
            ))}
          </div>
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <Trans>Loading...</Trans>
                ) : (
                  <Trans context="action_button">Load more</Trans>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Dialogs */}
      <CreateListDialog
        householdId={householdId}
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateSuccess}
      />

      <UpgradeDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature={t`Multiple Shopping Lists`}
        description={t`Create unlimited lists for different stores or meal plans.`}
        onUpgrade={() => void navigate({ to: '/upgrade' })}
      />
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
      <p className="text-muted-foreground">
        <Trans>Something went wrong loading your shopping lists.</Trans>
      </p>
      <Button variant="outline" onClick={onRetry}>
        <Trans context="action_button">Retry</Trans>
      </Button>
    </div>
  );
}
